import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  backfillDailySnapshots,
  generateRecap,
  useDashboardMetrics,
  useRecaps,
  useRevenueByDay,
  useTodaysVisits,
  rangeStartDate,
  type CustomRangeBounds,
  type RangeKey,
  type RecapKind,
  type RecapRow,
} from '@busy/api';
import { Panel } from '@busy/ui';
import { HelpHint } from '../components/HelpHint';
import {
  lastCompletedMonthBounds,
  lastCompletedQuarterBounds,
  lastCompletedWeekBounds,
  lastCompletedYearBounds,
} from '@busy/core';
import { RangeSelector } from '../components/dashboard/RangeSelector';
import { CapacityTile } from '../components/dashboard/CapacityTile';
import { RevenueChart } from '../components/dashboard/RevenueChart';
import { InsightsCard } from '../components/dashboard/InsightsCard';
import { RecentAchievementsTile } from '../components/dashboard/RecentAchievementsTile';
import { OperationalAveragesTile } from '../components/dashboard/OperationalAveragesTile';
import { TopMenuItemsTile } from '../components/dashboard/TopMenuItemsTile';
import { RevenueBreakdownModal } from '../components/dashboard/RevenueBreakdownModal';
import { RecapModal } from '../components/dashboard/RecapModal';
import { EodWrapupModal } from '../components/cutTimer/EodWrapupModal';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';

export function Dashboard() {
  // Default to TODAY — the most action-oriented window for a barber
  // mid-shift checking the dashboard ("am I on pace right now?"). The
  // Capacity tile's progress bar collapses to a daily view, the
  // Outlook stats are scoped to today, and the heatmap shows just
  // today's row. Wider ranges are one tap away in the selector for
  // weekly / monthly review.
  const [range, setRange] = useState<RangeKey>('today');
  // Custom-range bounds — populated only when range === 'custom'. The
  // RangeSelector's onChange supplies them. Set to null whenever the
  // user picks a preset so the hooks ignore them.
  const [customBounds, setCustomBounds] = useState<CustomRangeBounds | null>(null);
  function changeRange(next: RangeKey, custom?: CustomRangeBounds | null) {
    setRange(next);
    setCustomBounds(next === 'custom' ? custom ?? null : null);
  }
  const [openRecap, setOpenRecap] = useState<RecapRow | null>(null);
  // EOD wrap-up modal state — the chip used to live on Today; moved
  // here so the analytics surface owns retrospective summaries.
  const [showWrapup, setShowWrapup] = useState(false);
  // Revenue breakdown popup — opens when the Revenue metric card is
  // tapped. Replaces the prior scroll-to-chart behavior; the chart
  // link still lives inside the modal so it's one extra tap away.
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const t = useT();
  const language = useUI((s) => s.language);
  const navigate = useNavigate();

  // Refs for the metric-card scroll-to-section behavior. The Revenue
  // metric scrolls down to the Revenue-by-day chart, the Avg Ticket
  // metric scrolls to Capacity Utilization (where the chair-rate math
  // lives). Total / VIP / New client cards navigate to the Clients tab.
  const revenueRef = useRef<HTMLDivElement>(null);
  const capacityRef = useRef<HTMLDivElement>(null);
  function scrollTo(ref: React.RefObject<HTMLDivElement | null>) {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const metrics = useDashboardMetrics(range, customBounds);
  const revenueByDay = useRevenueByDay(range, customBounds);
  const todaysVisits = useTodaysVisits();
  // Recap archive — populated by overlap with the dashboard's active
  // window. Any recap whose period intersects [rangeStart, rangeEnd]
  // shows up. So:
  //   • range='today' → typically empty (no completed recap periods
  //     end on today)
  //   • range='7d'    → maybe 1 weekly recap if its period overlaps
  //   • range='30d'   → 1 monthly + 4ish weeklies in that span
  //   • range='ytd'   → all recaps generated this year
  //   • range='custom'→ recaps with periods that intersect the bounds
  // Previously this used a "last N days" heuristic that didn't match
  // the user's selected range.
  const recapsStart = ymdLocal(rangeStartDate(range, customBounds));
  const recapsEnd = (() => {
    if (range === 'custom' && customBounds) return customBounds.end;
    return ymdLocal(new Date());
  })();
  const recaps = useRecaps({ start: recapsStart, end: recapsEnd });

  // Lazy auto-generation. Once on Dashboard mount, check if the last
  // completed weekly / monthly / quarterly / annual recaps exist;
  // generate any that don't. Generation is idempotent at the DB layer
  // (unique constraint on user/kind/period_start) so a parallel mount
  // won't double-insert.
  //
  // Quarterly + annual landed in migration 0067. The annual recap
  // doubles as the tax-prep summary the user explicitly asked for —
  // one row that captures total revenue + total tips for the year.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const weekly = lastCompletedWeekBounds();
      const monthly = lastCompletedMonthBounds();
      const quarterly = lastCompletedQuarterBounds();
      const annual = lastCompletedYearBounds();
      // Fire all four. generateRecap short-circuits when the row exists
      // (single SELECT preflight), so subsequent mounts are cheap.
      await Promise.all([
        generateRecap({ kind: 'weekly', periodStart: weekly.start, periodEnd: weekly.end }),
        generateRecap({ kind: 'monthly', periodStart: monthly.start, periodEnd: monthly.end }),
        generateRecap({ kind: 'quarterly', periodStart: quarterly.start, periodEnd: quarterly.end }),
        generateRecap({ kind: 'annual', periodStart: annual.start, periodEnd: annual.end }),
        // Daily-snapshot backfill (migration 0068). Fire-and-forget;
        // failure here is invisible to the user since the snapshot
        // table powers analytics, not any rendered surface.
        backfillDailySnapshots(),
      ]);
      if (cancelled) return;
    })().catch(() => {
      // Silent fail — the user can still see existing recaps even if generation hits an error.
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Period-over-period deltas for the four metric tiles. The
  // useDashboardMetrics hook fetches both current + prior windows in
  // parallel so each tile just subtracts and labels — no extra fetch
  // needed here. Hidden entirely when both sides of the comparison
  // are zero (avoids a meaningless "no change" pill on a brand-new
  // account).
  const vsLabel = t(`dash.metric.delta.${range}`);
  const makeDelta = (
    current: number,
    prior: number,
    unit: 'currency' | 'count',
  ): MetricDelta => ({
    value: current - prior,
    unit,
    vsLabel,
    show: !(current === 0 && prior === 0),
  });
  // Delta tracks the same combined cuts + tips total the metric card
  // shows on the headline, so a +12 next to the number means +12 against
  // the same yardstick.
  //
  // Suppressed on custom ranges: there's no canonical "prior period"
  // for an arbitrary date pair — same-length-before? calendar-aligned
  // prior year? Picking one is misleading; we just hide the chip and
  // let the absolute number stand on its own.
  const showDeltas = range !== 'custom';
  const revenueDelta = metrics.data && showDeltas
    ? makeDelta(
        metrics.data.total_revenue_cents + metrics.data.total_tips_cents,
        metrics.data.prior_total_revenue_cents + metrics.data.prior_total_tips_cents,
        'currency',
      )
    : undefined;
  const cutsDelta = metrics.data && showDeltas
    ? makeDelta(metrics.data.total_visits, metrics.data.prior_total_visits, 'count')
    : undefined;
  const avgTicketDelta = metrics.data && showDeltas
    ? makeDelta(
        metrics.data.avg_ticket_cents,
        metrics.data.prior_avg_ticket_cents,
        'currency',
      )
    : undefined;
  const newClientsDelta = metrics.data && showDeltas
    ? makeDelta(metrics.data.new_clients, metrics.data.prior_new_clients, 'count')
    : undefined;

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-5 pb-24">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
            {t('dash.title')}
          </h1>
          <p className="text-[15px] text-ink-500 mt-1.5">
            {t(`range.${range}`)} · {formatRangeWindow(range, language, customBounds)}
          </p>
        </div>
        <RangeSelector value={range} customBounds={customBounds} onChange={changeRange} />
      </header>

      {/* Section order (per product spec):
          1. Your metrics — at-a-glance grid
          2. Recent achievements — scoped to the active range
          3. + For you — derived insights from the chart
          4. Revenue by day — the chart itself
          5. Capacity utilization — target hours + Week At A Glance heatmap
          6. Today's wrap-up — same-day chip (only renders when there are cuts logged today)
          7. Past recaps — archive of weekly/monthly snapshots

          (Client tiers breakdown removed — it lives as the filter UI on
          the Clients tab now, no need to duplicate the view here.) */}

      {/* 1. Your metrics */}
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2.5 inline-flex items-center gap-1.5">
          <span>{t('dash.metricsHeader')}</span>
          <HelpHint
            titleKey="dash.metricsHeader.help.title"
            bodyKey="dash.metricsHeader.help.body"
            ariaKey="dash.metricsHeader.help.aria"
          />
        </div>
        <section className="grid grid-cols-2 gap-3 md:gap-[18px]">
          <MetricCard
            label={t('dash.metric.revenue')}
            // Headline = cuts + tips. The breakdown modal exposes the
            // split when the barber wants to know how much of the
            // headline number is gratuity vs service economics.
            value={`$${(
              ((metrics.data?.total_revenue_cents ?? 0) +
                (metrics.data?.total_tips_cents ?? 0)) /
              100
            ).toFixed(0)}`}
            loading={metrics.loading}
            accent="red"
            onClick={() => setShowRevenueBreakdown(true)}
            delta={revenueDelta}
          />
          <MetricCard
            label={t('dash.metric.cuts')}
            value={metrics.data?.total_visits ?? 0}
            loading={metrics.loading}
            accent="gold"
            onClick={() => navigate(`/cuts?range=${range}`)}
            delta={cutsDelta}
          />
          <MetricCard
            label={t('dash.metric.avgTicket')}
            value={`$${((metrics.data?.avg_ticket_cents ?? 0) / 100).toFixed(0)}`}
            loading={metrics.loading}
            accent="gold"
            onClick={() => scrollTo(capacityRef)}
            delta={avgTicketDelta}
          />
          <MetricCard
            label={t('dash.metric.newClients')}
            value={metrics.data?.new_clients ?? 0}
            loading={metrics.loading}
            accent="navy"
            onClick={() => navigate('/clients')}
            delta={newClientsDelta}
            helpKeys={{
              titleKey: 'dash.metric.newClients.help.title',
              bodyKey: 'dash.metric.newClients.help.body',
              ariaKey: 'dash.metric.newClients.help.aria',
            }}
          />
        </section>
      </div>

      {/* 2. Averages — small per-day / per-cut / per-client numbers a
          barber needs to spot operational patterns. Collapsible by
          default so the dashboard stays scannable; the trigger
          headline surfaces the three most-glanceable averages so the
          gist comes through even without expanding. Range-aware. */}
      <OperationalAveragesTile range={range} customBounds={customBounds} />

      {/* Note: the retention pair (RegularConversionTile +
          ClientChurnTile) lives on the Clients tab now. They're
          per-client metrics anchored on each client's own history
          (range-independent), so the Clients tab is the natural
          home — they fit alongside the Acquisition Channel + Top
          Clients tiles for the "who's in your roster" narrative. */}

      {/* Recent achievements — filtered to the selected range so the
          chip set matches what was earned in the same window the rest
          of the dashboard's numbers describe. */}
      <RecentAchievementsTile range={range} customBounds={customBounds} />

      {/* 3. + For you — derived insights, only renders when chart data exists */}
      {revenueByDay.data && <InsightsCard days={revenueByDay.data} />}

      {/* 4. Revenue by day */}
      <div ref={revenueRef} className="scroll-mt-20">
        <Panel title={t('dash.chart.title')} sub={t('dash.chart.sub')}>
          {revenueByDay.loading && <div className="h-48 rounded-2xl bg-bg-alt animate-pulse" />}
          {revenueByDay.error && (
            <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
              {revenueByDay.error}
            </div>
          )}
          {revenueByDay.data && <RevenueChart days={revenueByDay.data} />}
        </Panel>
      </div>

      {/* 5. Capacity utilization — actual vs target hours, missed revenue,
          plus the Week At A Glance heatmap (scoped by the range selector). */}
      <div ref={capacityRef} className="scroll-mt-20">
        <CapacityTile range={range} customBounds={customBounds} />
      </div>

      {/* 6. Top-requested services + add-ons for the selected range.
          Uses the same gold-crown vocabulary as the cut-timer pickers
          and the client profile so the visual cue is consistent — gold
          crown always means "the favorite." Auto-hides when there's no
          signal (no visits in range), so an empty today doesn't render
          a hollow tile. */}
      <TopMenuItemsTile range={range} customBounds={customBounds} />

      {/* (Client Tiers breakdown removed from the Dashboard — the same
          tile is the canonical filter UI on the Clients tab and was
          duplicating the view here.) */}

      {/* 6. Today's wrap-up — same-day chip, only when at least one cut
          has been logged today. Moved here from the Today screen so the
          analytics surface owns retrospective summaries. The same
          EodWrapupModal opens — same modal the Cut Timer uses. */}
      {todaysVisits.data && todaysVisits.data.length > 0 && (
        <button
          type="button"
          onClick={() => setShowWrapup(true)}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border border-line bg-bg-alt text-sm font-bold text-ink-700 hover:bg-line transition-colors"
        >
          <BarChartIcon className="w-4 h-4" />
          {t('today.wrapup.cta')}
          <span className="text-ink-500 font-normal">
            · {todaysVisits.data.length}{' '}
            {todaysVisits.data.length === 1
              ? t('today.wrapup.cuts_one')
              : t('today.wrapup.cuts_other')}
          </span>
        </button>
      )}

      {/* 7. Past recaps archive — week/month/quarter/annual snapshots
          whose period overlaps the active dashboard range. Hidden when
          empty (e.g. range='today' typically matches nothing). */}
      {recaps.data && recaps.data.length > 0 && (
        <Panel
          title={t('recap.archive.title')}
          sub={t('recap.archive.sub')}
        >
          <ul className="divide-y divide-line">
            {recaps.data.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setOpenRecap(r)}
                  className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-bg-alt/40 -mx-3 px-3 rounded-lg"
                >
                  <div className="min-w-0">
                    <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-gold-500">
                      {t(
                        r.kind === 'weekly'
                          ? 'recap.eyebrow.weekly'
                          : r.kind === 'monthly'
                            ? 'recap.eyebrow.monthly'
                            : r.kind === 'quarterly'
                              ? 'recap.eyebrow.quarterly'
                              : 'recap.eyebrow.annual',
                      )}
                    </div>
                    <div className="text-sm font-bold mt-0.5 truncate">
                      {formatRecapPeriodShort(r.period_start, r.period_end, r.kind, language)}
                    </div>
                  </div>
                  <RecapPreviewStats data={r.data} />
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {openRecap && <RecapModal recap={openRecap} onClose={() => setOpenRecap(null)} />}
      {showWrapup && <EodWrapupModal onClose={() => setShowWrapup(false)} />}
      {showRevenueBreakdown && (
        <RevenueBreakdownModal
          rangeLabel={t(`range.${range}`)}
          cutsRevenueCents={metrics.data?.total_revenue_cents ?? 0}
          tipsCents={metrics.data?.total_tips_cents ?? 0}
          visitsInRange={metrics.data?.total_visits ?? 0}
          onClose={() => setShowRevenueBreakdown(false)}
          onViewChart={() => {
            setShowRevenueBreakdown(false);
            // Wait one tick so the modal's exit animation isn't fighting
            // the page scroll; scrollIntoView prefers a settled DOM.
            requestAnimationFrame(() => scrollTo(revenueRef));
          }}
        />
      )}
    </div>
  );
}

function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

function RecapPreviewStats({ data }: { data: Record<string, unknown> }) {
  const totals = (data?.totals ?? {}) as Record<string, number>;
  const cuts = totals.cuts ?? 0;
  const revDollars = Math.round((totals.revenue_cents ?? 0) / 100);
  return (
    <div className="text-right shrink-0">
      <div className="text-sm font-bold tabular-nums">${revDollars.toLocaleString()}</div>
      <div className="text-[11px] text-ink-500">
        {cuts} {cuts === 1 ? 'cut' : 'cuts'}
      </div>
    </div>
  );
}

function formatRecapPeriodShort(
  start: string,
  end: string,
  kind: RecapKind,
  language: 'en' | 'es',
): string {
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  if (kind === 'annual') {
    return String(s.getFullYear());
  }
  if (kind === 'quarterly') {
    const q = Math.floor(s.getMonth() / 3) + 1;
    return `Q${q} ${s.getFullYear()}`;
  }
  if (kind === 'monthly') {
    return s.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  const sameMonth = s.getMonth() === e.getMonth();
  if (sameMonth) {
    return `${s.toLocaleDateString(locale, { month: 'short' })} ${s.getDate()}–${e.getDate()}`;
  }
  return `${s.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`;
}

// "Apr 5 – May 4" style label for the active range window. Today range collapses
// to a single date. Custom range uses the user-picked bounds.
/** Local-date YYYY-MM-DD — matches the format the recaps table stores
 *  in period_start / period_end. Used by the recap-archive filter so
 *  the date strings are directly comparable. */
function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRangeWindow(
  range: RangeKey,
  language: 'en' | 'es',
  custom?: CustomRangeBounds | null,
): string {
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const start = rangeStartDate(range, custom);
  const end = range === 'custom' && custom
    ? new Date(custom.end + 'T00:00:00')
    : (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
      })();
  const startLabel = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  if (range === 'today') return endLabel;
  if (start.getTime() === end.getTime()) return endLabel;
  return `${startLabel} – ${endLabel}`;
}

/** Period delta passed to a MetricCard. The hook owns the math; the
 *  tile just formats. `unit` controls whether to render with a "$"
 *  prefix (currency metrics — revenue, avg ticket) or a plain number
 *  (counts — cuts, new clients). */
type MetricDelta = {
  /** Signed value: current - prior. Already in whatever unit you pass
   *  (cents for currency, integer for counts). */
  value: number;
  unit: 'currency' | 'count';
  /** Translated trailing label, e.g. "vs prev 7d". Already i18n'd by
   *  the caller so MetricCard stays unit-agnostic. */
  vsLabel: string;
  /** Hide the delta when there's no prior period to compare against
   *  (both prior + current are 0). Set by the caller. */
  show: boolean;
};

function MetricCard({
  label,
  value,
  loading,
  accent = 'gold',
  onClick,
  ariaLabel,
  delta,
  helpKeys,
}: {
  label: string;
  value: string | number;
  loading: boolean;
  accent?: 'gold' | 'red' | 'navy';
  /** When provided, the card renders as a button that navigates / scrolls
   *  the dashboard to the matching deeper view. Without it, the card is
   *  a plain div (current behavior preserved for any future read-only
   *  metrics). */
  onClick?: () => void;
  ariaLabel?: string;
  /** Period-over-period delta. When set, a small colored pill renders
   *  below the value showing the absolute change vs the prior window. */
  delta?: MetricDelta;
  /** Optional (?) icon next to the label that opens an explainer modal.
   *  Used on metrics whose definition isn't obvious from the label —
   *  e.g. "New Clients" needs to disambiguate "first haircut in range"
   *  from "contact added in range." */
  helpKeys?: { titleKey: string; bodyKey: string; ariaKey: string };
}) {
  if (loading) return <div className="h-28 rounded-xl bg-bg-alt animate-pulse" />;
  const accentColor =
    accent === 'red' ? 'bg-red-600' : accent === 'navy' ? 'bg-navy-700' : 'bg-gold-500';
  const inner = (
    <>
      {/* 3px accent bar on the left edge — gold by default, mirrors demo's .metric::before */}
      <span aria-hidden className={`absolute top-0 left-0 bottom-0 w-[3px] ${accentColor}`} />
      <div className="text-[12px] uppercase tracking-[0.1em] font-bold text-ink-500 mb-1.5 inline-flex items-center gap-1.5">
        <span>{label}</span>
        {helpKeys && (
          <HelpHint
            titleKey={helpKeys.titleKey}
            bodyKey={helpKeys.bodyKey}
            ariaKey={helpKeys.ariaKey}
          />
        )}
      </div>
      <div className="font-display text-[34px] md:text-[42px] text-navy-900 dark:text-gold-500 leading-none tabular-nums">
        {value}
      </div>
      {delta && delta.show && <MetricDeltaPill delta={delta} />}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        className="relative rounded-2xl border border-line bg-surface p-5 shadow-xs overflow-hidden text-left hover:border-navy-900 dark:hover:border-gold-500 active:scale-[0.99] transition-all"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="relative rounded-2xl border border-line bg-surface p-5 shadow-xs overflow-hidden">
      {inner}
    </div>
  );
}

/** Formats the absolute delta with an arrow + colored chip. Negative
 *  values render with a flipped arrow + red tone; zero renders a flat
 *  dash so the absence of change reads as a deliberate "no change"
 *  rather than "we forgot to render it." Currency deltas show a "$"
 *  prefix, counts stay plain. */
function MetricDeltaPill({ delta }: { delta: MetricDelta }) {
  const sign = delta.value > 0 ? '+' : delta.value < 0 ? '−' : '';
  const abs = Math.abs(delta.value);
  const formatted =
    delta.unit === 'currency'
      ? `$${Math.round(abs / 100).toLocaleString()}`
      : abs.toLocaleString();
  const tone =
    delta.value > 0
      ? 'text-emerald-700 dark:text-emerald-400'
      : delta.value < 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-ink-500';
  const arrow =
    delta.value > 0 ? '▲' : delta.value < 0 ? '▼' : '—';
  return (
    <div className="mt-3 text-[11px] tabular-nums">
      <span className={`font-extrabold ${tone}`}>
        {arrow} {sign}{formatted}
      </span>{' '}
      <span className="text-ink-500">· {delta.vsLabel}</span>
    </div>
  );
}
